// ─── Conversion palier prop firm : formulaire <-> colonnes Prisma ─────────────
//
// Module PUR (pas de React, pas de Wasp, pas de Prisma) : le schéma continue de
// stocker chaque règle en deux colonnes (xxxPercent / xxxAmount), mais le
// formulaire les fusionne en un seul champ porteur de son unité pour ne plus
// jamais laisser les deux remplies en silence. Contexte de l'incident qui a
// motivé ce module : `src/trading-accounts/tierRuleValidation.ts`.

import { normalizePricingModel, type PricingModel } from "../../../trading-accounts/pricingModel";
import {
  DRAWDOWN_LOCK_MODES,
  DRAWDOWN_LOCK_SCOPES,
  type DrawdownLockMode,
  type DrawdownLockScope,
} from "../../../trading-accounts/drawdownLock";

/** Ligne d'édition d'une surcharge par préfixe symbole. Saisie brute. */
export interface SymbolCommissionRow {
  prefix: string;
  rate: string;
}

/** Une règle exprimable en pourcentage OU en montant, jamais les deux. */
export interface RuleValue {
  unit: "percent" | "amount";
  /** Saisie brute. Vide = règle non renseignée. */
  value: string;
}

export const emptyRule = (): RuleValue => ({ unit: "percent", value: "" });

/**
 * Choisit l'unité d'affichage d'une règle stockée en deux colonnes.
 *
 * Le MONTANT l'emporte quand les deux sont remplis, par parité avec
 * `propFirmEngine.ts` (lignes 350 et 444) qui résout `xxxAmount` en premier et
 * ignore alors `xxxPercent`. Afficher le pourcentage ici montrerait une valeur
 * que le moteur n'applique pas.
 */
export function ruleFromColumns(percent: number | null, amount: number | null): RuleValue {
  if (amount !== null && amount !== undefined) return { unit: "amount", value: String(amount) };
  if (percent !== null && percent !== undefined) return { unit: "percent", value: String(percent) };
  return emptyRule();
}

/** Une valeur numerique propre, avec virgule francaise optionnelle, ex. "3", "3.5", "3,5". Pas de "%", pas de texte, pas de separateur de milliers. */
const CLEAN_NUMBER_RE = /^-?\d+(?:[.,]\d+)?$/;

/**
 * Un separateur (point ou virgule) suivi d'exactement trois chiffres, ex.
 * "1,000" ou "50.000". C'est la forme structurellement ambigue entre un
 * separateur de milliers anglais ("1,000" = mille) et une decimale francaise
 * ("1,000" = un). Deux chiffres après le separateur ("3,25") ne sont PAS
 * ambigus : aucune des deux notations n'ecrit une decimale a trois chiffres
 * pour un centime ou un dixieme de pourcent.
 */
const AMBIGUOUS_THOUSANDS_RE = /^-?\d+[.,]\d{3}$/;

/**
 * Parse une saisie numerique libre selon une politique unique, partagee par
 * `ruleToColumns` (les cinq paires percent/amount) ET par les champs simples
 * de `tierFormToData` (`accountSize`, `activationFee`, `consistencyRulePercent`).
 * `null` = saisie vide OU ambigue : dans les deux cas le champ reste non
 * renseigne plutot que de porter un chiffre faux.
 *
 * WHY : `parseFloat` a ete deliberement ecarte ici. `parseFloat("3,5")` rend
 * `3` (il s'arrete a la virgule) et `parseFloat("5%")` rend `5` (il s'arrete
 * au caractere non numerique) : les deux TRONQUENT silencieusement une saisie
 * plutot que de signaler qu'elle est ambigue. C'est exactement le mode de
 * corruption silencieuse qui a produit l'incident Lucid documente dans
 * `tierRuleValidation.ts` (8 paliers avec la mauvaise colonne remplie, jamais
 * detectes avant l'archivage de 18 comptes cramés).
 *
 * Politique retenue : normaliser la virgule decimale francaise (le product
 * owner est francophone et tape naturellement "3,5"), puis exiger un nombre
 * PROPRE après normalisation via `CLEAN_NUMBER_RE` plutot que `Number()` seul
 * (qui accepterait aussi "1e3" ou " 5 " sans que ce soit voulu ici). Toute
 * saisie ambigue ("5%", "5abc", "1 000") rend la valeur nulle plutot qu'une
 * valeur tronquee : mieux vaut un champ vide visible qu'un chiffre plausible
 * et faux. Une valeur negative est rejetee de la meme façon : ni une regle de
 * palier ni un montant/pourcentage de ce module n'a de sens negatif.
 *
 * ⚠️ Un groupe de TROIS chiffres apres un separateur ("1,000", "50.000") est
 * refuse par `AMBIGUOUS_THOUSANDS_RE`, meme s'il passe `CLEAN_NUMBER_RE`. Les
 * admins copient couramment `accountSize` depuis le site d'une prop firm
 * anglophone ("$50,000"), qui utilise la virgule comme separateur de
 * milliers ; `Number("50,000".replace(",", "."))` donnerait silencieusement
 * `50` au lieu de `50000`. `accountSize` sert de base a l'objectif du
 * challenge (`propFirmEngine.ts:236`) : une troncature ici ne casse pas
 * qu'un affichage, elle corrompt tout le calcul en aval. Deviner l'intention
 * n'est pas acceptable pour ce champ : "1,000" reste un vrai un en notation
 * francaise, donc le module refuse plutot que de choisir a la place de
 * l'admin.
 *
 * `Number("")` vaut `0` : le cas vide est donc traité à part, AVANT l'appel à
 * `Number`, pour ne pas confondre "non renseigné" et "zéro".
 *
 * Deux copies de cette politique dériveraient inévitablement l'une de
 * l'autre : c'est pourquoi `ruleToColumns` s'exprime en termes de ce helper
 * plutôt que de dupliquer la regex.
 */
export function parseNumericInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  if (!CLEAN_NUMBER_RE.test(trimmed)) return null;
  if (AMBIGUOUS_THOUSANDS_RE.test(trimmed)) return null;
  const parsed = Number(trimmed.replace(",", "."));
  if (Number.isNaN(parsed) || parsed < 0) return null;
  return parsed;
}

/**
 * Formate un prix stocke (colonne `Float`) en chaine que `parseNumericInput`
 * accepte a coup sur au prochain enregistrement.
 *
 * PIEGE CORRIGE ICI (revue Task 12) : `tier.price?.toString()` sur un prix a
 * TROIS decimales ou plus (ex. `149.995`, importable via le CSV du
 * referentiel ou un arrondi flottant) produit une chaine qu'`AMBIGUOUS_THOUSANDS_RE`
 * rejette -- exactement la meme regex qui protege `accountSize` d'un
 * separateur de milliers anglais. Un admin qui ouvre puis enregistre un tel
 * palier SANS toucher au prix le verrait silencieusement mis a `null` (voir
 * `parseNumericInput` : une saisie ambigue rend `null`, pas une valeur
 * tronquee). Arrondir au centime AU CHARGEMENT elimine la classe entiere :
 * un prix est une donnee monetaire, jamais legitimement precise au-dela du
 * centime.
 */
export function formatPriceForForm(price: number | null | undefined): string {
  if (price === null || price === undefined) return "";
  const rounded = Math.round((price + Number.EPSILON) * 100) / 100;
  return rounded.toString();
}

/** Reconstitue le couple de colonnes attendu par l'API. Politique de parsing : voir `parseNumericInput`. */
export function ruleToColumns(rule: RuleValue): { percent: number | null; amount: number | null } {
  const parsed = parseNumericInput(rule.value);
  if (parsed === null) return { percent: null, amount: null };
  return rule.unit === "amount" ? { percent: null, amount: parsed } : { percent: parsed, amount: null };
}

/**
 * Lignes d'édition -> colonne Json. Une ligne sans préfixe ou dont le taux ne
 * passe pas `parseNumericInput` (vide, "abc", négatif, milliers ambigus) est
 * ÉCARTÉE plutôt que tronquée — même politique que le reste du formulaire.
 * Préfixes en MAJUSCULES : `resolveCommissionForSymbol` compare en majuscules
 * et `mergeCommissionSources` normalise de même.
 */
export function symbolRowsToRecord(rows: SymbolCommissionRow[]): Record<string, number> | null {
  const entries = rows
    .map((r) => [r.prefix.trim().toUpperCase(), parseNumericInput(r.rate)])
    .filter((e): e is [string, number] => e[0] !== "" && e[1] !== null);
  return entries.length > 0 ? Object.fromEntries(entries) : null;
}

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Identite CLIENT d'un palier, pour servir de cle React stable.
 *
 * POURQUOI : la liste de paliers etait rendue avec `key={index}`. Tant que les
 * paliers n'etaient qu'AJOUTES en fin de liste, la position valait identite et
 * c'etait sans consequence. La duplication insere une copie AU MILIEU de la
 * liste : toutes les positions suivantes se decalent, React reconcilie alors
 * des composants par position et rattache l'etat local d'un palier au VOISIN.
 * Ce projet s'est deja fait mordre par ce motif exact (modale d'ajout de
 * compte : `key={index}`, lignes decalees, portails Radix demontes en pleine
 * interaction et UI crashee).
 *
 * Ce champ ne part JAMAIS au serveur : `tierFormToData` construit un objet
 * explicite champ par champ (aucun spread), donc une colonne inconnue ne peut
 * pas atteindre Prisma. Un test verrouille cette propriete.
 *
 * `crypto.randomUUID` n'existe que dans un contexte SECURISE (HTTPS ou
 * localhost) : un dev servi en HTTP simple le verrait `undefined` et planterait
 * a la creation d'un palier. Le repli aleatoire est donc necessaire, et
 * suffisant puisqu'il ne s'agit que d'une cle de rendu et non d'un jeton.
 */
export function newClientKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `tier-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

export interface TierForm {
  id?: string;
  /** Identite client pour la cle React. Jamais persistee. Voir `newClientKey`. */
  clientKey?: string;
  label: string;
  minBalance: number;
  maxBalance: number;
  /** Taille nominale du compte à l'achat. Vide = non renseignée. */
  accountSize: string;
  sortOrder: number;
  // Challenge
  profitTarget: RuleValue;
  minTradingDays: string;
  // Drawdown
  drawdownType: "" | "intraday" | "eod" | "static";
  hasTrailingDrawdown: boolean;
  maxDailyLoss: RuleValue;
  maxTotalLoss: RuleValue;
  trailingDrawdown: RuleValue;
  // Funded / payout
  payoutThreshold: RuleValue;
  payoutCooldownDays: string;
  activationFee: string;
  // Consistance : challenge et funded sont souvent differentes
  consistencyRulePercent: string;
  fundedConsistencyRulePercent: string;
  // Extra
  maxContracts: string;
  /** Prix du palier (mensuel pour un abonnement). Vide = non renseigne. */
  price: string;
  // Commissions round-trip du palier (surcharge du niveau firm, cas TPT PRO+)
  commissionPerContract: string;
  symbolCommissionRows: SymbolCommissionRow[];
}

export interface AccountTypeForm {
  id?: string;
  /**
   * Identite client pour la cle React. Jamais persistee. Voir `newClientKey`.
   *
   * Meme motif que sur `TierForm`, un cran au-dessus : SUPPRIMER un type de
   * compte decale tous les suivants, et chaque sous-arbre
   * `AccountTypeEditor` contient des `PhaseEditor` portant des formulaires de
   * phase NON enregistres. Avec une cle positionnelle, React rattacherait ces
   * brouillons au type de compte VOISIN.
   */
  clientKey?: string;
  name: string;
  slug: string;
  sortOrder: number;
  isActive: boolean;
  pricingModel: PricingModel;
  /**
   * Gel du plancher (voir drawdownLock.ts). Chaine vide = defaut historique
   * (+100 chez Apex, 0 ailleurs ; gel au trailing ; funded seulement).
   */
  drawdownLockOffset: string;
  drawdownLockMode: "" | DrawdownLockMode;
  drawdownLockScope: "" | DrawdownLockScope;
  tiers: TierForm[];
}

export const emptyTier = (sortOrder = 0): TierForm => ({
  clientKey: newClientKey(),
  label: "",
  minBalance: 0,
  maxBalance: 9999999,
  accountSize: "",
  sortOrder,
  profitTarget: emptyRule(),
  minTradingDays: "",
  drawdownType: "",
  hasTrailingDrawdown: false,
  maxDailyLoss: emptyRule(),
  maxTotalLoss: emptyRule(),
  trailingDrawdown: emptyRule(),
  payoutThreshold: emptyRule(),
  payoutCooldownDays: "",
  activationFee: "",
  consistencyRulePercent: "",
  fundedConsistencyRulePercent: "",
  maxContracts: "",
  price: "",
  commissionPerContract: "",
  symbolCommissionRows: [],
});

export const emptyAccountType = (sortOrder = 0): AccountTypeForm => ({
  clientKey: newClientKey(),
  name: "",
  slug: "",
  sortOrder,
  isActive: true,
  pricingModel: "one_time",
  drawdownLockOffset: "",
  drawdownLockMode: "",
  drawdownLockScope: "",
  tiers: [emptyTier(0)],
});

/**
 * Phases de chaque palier d'un template SERVEUR, indexees par id de palier.
 *
 * POURQUOI les phases ne vivent PAS dans `TierForm` : le formulaire est un
 * brouillon que l'admin edite en memoire jusqu'au submit, alors que les phases
 * sont ecrites une par une par leur propre action (`upsertPropFirmPhase`) et
 * appartiennent au serveur. Les copier dans le brouillon a l'ouverture les
 * figerait : une phase creee ensuite n'apparaitrait qu'a la reouverture du
 * template. C'est exactement ce qui se passait, en pire : `tierToForm` ne les
 * recopiait meme pas, donc la liste etait TOUJOURS vide et l'admin concluait
 * que sa phase n'avait pas ete creee.
 *
 * On les lit donc a chaque rendu depuis le template frais de la query, dont le
 * cache est invalide par Wasp apres chaque action touchant `PropFirmPhase`.
 * Un palier sans phase, ou pas encore en base (pas d'id), n'a pas d'entree :
 * l'appelant retombe sur `[]`.
 */
export function phasesByTierId(template: any): Map<string, any[]> {
  const map = new Map<string, any[]>();
  for (const at of template?.accountTypes ?? []) {
    for (const tier of at?.tiers ?? []) {
      if (!tier?.id) continue;
      // Copie avant tri : l'objet vient du cache de la query, on ne le mute pas.
      const phases = [...(tier.phases ?? [])].sort(
        (a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
      );
      map.set(tier.id, phases);
    }
  }
  return map;
}

export function tierToForm(tier: any): TierForm {
  return {
    id: tier.id,
    // Un palier sauvegarde porte deja un `id` unique, mais on genere tout de
    // meme une cle client : un seul schema d'identite se raisonne plus
    // facilement que deux, et la cle reste valable pour un palier neuf comme
    // pour un palier existant.
    clientKey: newClientKey(),
    label: tier.label,
    minBalance: tier.minBalance,
    maxBalance: tier.maxBalance,
    accountSize: tier.accountSize?.toString() ?? "",
    sortOrder: tier.sortOrder,
    profitTarget: ruleFromColumns(tier.profitTargetPercent, tier.profitTargetAmount),
    minTradingDays: tier.minTradingDays?.toString() ?? "",
    drawdownType: (["intraday", "eod", "static"].includes(tier.drawdownType) ? tier.drawdownType : "") as TierForm["drawdownType"],
    hasTrailingDrawdown: tier.hasTrailingDrawdown,
    maxDailyLoss: ruleFromColumns(tier.maxDailyLossPercent, tier.maxDailyLossAmount),
    maxTotalLoss: ruleFromColumns(tier.maxTotalLossPercent, tier.maxTotalLossAmount),
    trailingDrawdown: ruleFromColumns(tier.trailingDrawdownPercent, tier.trailingDrawdownAmount),
    payoutThreshold: ruleFromColumns(tier.payoutThresholdPercent, tier.payoutThresholdAmount),
    payoutCooldownDays: tier.payoutCooldownDays?.toString() ?? "",
    activationFee: tier.activationFee?.toString() ?? "",
    consistencyRulePercent: tier.consistencyRulePercent?.toString() ?? "",
    fundedConsistencyRulePercent: tier.fundedConsistencyRulePercent?.toString() ?? "",
    maxContracts: tier.maxContracts?.toString() ?? "",
    price: formatPriceForForm(tier.price),
    commissionPerContract: tier.commissionPerContract?.toString() ?? "",
    symbolCommissionRows:
      tier.symbolCommissions && typeof tier.symbolCommissions === "object"
        ? Object.entries(tier.symbolCommissions as Record<string, number>).map(
            ([prefix, rate]) => ({ prefix, rate: String(rate) }),
          )
        : [],
  };
}

export function accountTypeToForm(at: any): AccountTypeForm {
  return {
    id: at.id,
    // Meme raisonnement que dans `tierToForm` : un type de compte sauvegarde
    // porte deja un `id`, mais on genere tout de meme une cle client pour
    // n'avoir qu'UN seul schema d'identite a raisonner, valable pour un type
    // neuf comme pour un type existant.
    clientKey: newClientKey(),
    name: at.name,
    slug: at.slug,
    sortOrder: at.sortOrder,
    isActive: at.isActive,
    pricingModel: normalizePricingModel(at.pricingModel),
    drawdownLockOffset: at.drawdownLockOffset != null ? String(at.drawdownLockOffset) : "",
    drawdownLockMode: (DRAWDOWN_LOCK_MODES as readonly string[]).includes(at.drawdownLockMode)
      ? at.drawdownLockMode
      : "",
    drawdownLockScope: (DRAWDOWN_LOCK_SCOPES as readonly string[]).includes(at.drawdownLockScope)
      ? at.drawdownLockScope
      : "",
    tiers: at.tiers.map(tierToForm),
  };
}

/**
 * Colonnes de gel a envoyer au serveur. Toujours envoyees (null = defaut) :
 * vider un champ dans le formulaire doit remettre le defaut en base.
 */
export function drawdownLockFormToData(at: AccountTypeForm) {
  const offset = at.drawdownLockOffset.trim() === "" ? null : Number(at.drawdownLockOffset);
  return {
    drawdownLockOffset: offset !== null && Number.isFinite(offset) ? offset : null,
    drawdownLockMode: at.drawdownLockMode === "" ? null : at.drawdownLockMode,
    drawdownLockScope: at.drawdownLockScope === "" ? null : at.drawdownLockScope,
  };
}

/**
 * Copie un palier pour saisie rapide d'une variante.
 *
 * `id` et `phases` sont ECARTES : la copie est un palier neuf, non encore en
 * base. Le `clientKey` est REGENERE (et non conserve) : deux paliers de meme
 * cle React se reconcilieraient l'un sur l'autre. Copier les phases supposerait de les ecrire avant que l'admin ait
 * valide le palier, puisqu'elles ne sont editables qu'une fois le tier
 * sauvegarde.
 *
 * Les cinq champs `RuleValue` (profitTarget, maxDailyLoss, maxTotalLoss,
 * trailingDrawdown, payoutThreshold) sont des objets imbriques : un simple
 * spread de `source` copierait la REFERENCE, pas la valeur. Editer le
 * `profitTarget` de la copie muterait alors silencieusement celui du palier
 * source. Chacun est donc explicitement recree en objet neuf.
 *
 * Le label est suffixe car `@@unique([propFirmAccountTypeId, label])` rejette
 * deux paliers de meme nom dans un meme type de compte. La comparaison avec
 * `existingLabels` trime aussi chaque cote : `tierFormToData` trime deja le
 * label au moment de l'ecriture (donc les labels en base sont propres), mais
 * cette fonction ne doit pas dependre de cette hypothese sur l'appelant — un
 * `existingLabels` construit autrement (formulaire pas encore sauvegarde,
 * saisie manuelle) pourrait porter des espaces parasites qui feraient rater
 * la collision et produire un doublon visuellement identique.
 */
export function duplicateTier(source: TierForm, existingLabels: string[]): TierForm {
  const { id: _id, ...rest } = source as TierForm & { phases?: unknown };
  delete (rest as { phases?: unknown }).phases;

  // Un label source vide donnerait " (copie)" (espace en tete) : "Palier"
  // sert de nom neutre de repli plutot que de laisser passer ce defaut.
  const base = source.label.trim() || "Palier";
  const trimmedExisting = existingLabels.map((l) => l.trim());
  let candidate = `${base} (copie)`;
  let n = 2;
  while (trimmedExisting.includes(candidate)) {
    candidate = `${base} (copie ${n})`;
    n += 1;
  }

  return {
    ...(rest as TierForm),
    // ⚠️ `rest` porte le `clientKey` de la SOURCE : sans cet ecrasement
    // explicite, la copie naitrait avec la cle de son original et deux paliers
    // partageraient la meme cle React. C'est precisement le defaut que la cle
    // stable vient corriger, il serait donc reintroduit par la duplication
    // elle-meme, le seul chemin qui insere au milieu de la liste.
    clientKey: newClientKey(),
    label: candidate,
    profitTarget: { ...source.profitTarget },
    maxDailyLoss: { ...source.maxDailyLoss },
    maxTotalLoss: { ...source.maxTotalLoss },
    trailingDrawdown: { ...source.trailingDrawdown },
    payoutThreshold: { ...source.payoutThreshold },
    symbolCommissionRows: source.symbolCommissionRows.map((r) => ({ ...r })),
  };
}

export function tierFormToData(f: TierForm, i: number) {
  const isTrailing = f.drawdownType === "intraday" || f.drawdownType === "eod";
  const profitTarget = ruleToColumns(f.profitTarget);
  const maxDailyLoss = ruleToColumns(f.maxDailyLoss);
  const maxTotalLoss = ruleToColumns(f.maxTotalLoss);
  const trailingDrawdown = ruleToColumns(f.trailingDrawdown);
  const payoutThreshold = ruleToColumns(f.payoutThreshold);
  return {
    // Identite du palier DEJA en base. Indispensable : sans elle le serveur ne
    // peut pas distinguer un palier conserve d'un palier neuf, et les recree
    // tous — ce qui supprime leurs phases en cascade. Voir
    // `propFirmTemplateReconcile.ts`. `undefined` pour un palier neuf, jamais
    // `null` : le schema Zod attend un champ optionnel.
    //
    // ⚠️ Projection EXPLICITE, pas un spread : `clientKey` est une cle React
    // client-only et ne doit jamais partir au serveur.
    ...(f.id ? { id: f.id } : {}),
    // Trime avant ecriture : un label avec espaces parasites (copie/collage,
    // Tab en fin de champ) passerait la contrainte @@unique([...]) tout en
    // etant visuellement indiscernable d'un palier existant, produisant un
    // doublon invisible pour l'admin.
    label: f.label.trim(),
    minBalance: f.minBalance,
    maxBalance: f.maxBalance,
    accountSize: parseNumericInput(f.accountSize),
    sortOrder: i,
    profitTargetPercent: profitTarget.percent,
    profitTargetAmount: profitTarget.amount,
    minTradingDays: f.minTradingDays ? parseInt(f.minTradingDays) : null,
    drawdownType: (f.drawdownType === "" ? null : f.drawdownType) as "intraday" | "eod" | "static" | null,
    hasTrailingDrawdown: isTrailing,
    maxDailyLossPercent: maxDailyLoss.percent,
    maxDailyLossAmount: maxDailyLoss.amount,
    maxTotalLossPercent: maxTotalLoss.percent,
    maxTotalLossAmount: maxTotalLoss.amount,
    trailingDrawdownPercent: trailingDrawdown.percent,
    trailingDrawdownAmount: trailingDrawdown.amount,
    payoutThresholdPercent: payoutThreshold.percent,
    payoutThresholdAmount: payoutThreshold.amount,
    payoutCooldownDays: f.payoutCooldownDays ? parseInt(f.payoutCooldownDays) : null,
    activationFee: parseNumericInput(f.activationFee),
    consistencyRulePercent: parseNumericInput(f.consistencyRulePercent),
    fundedConsistencyRulePercent: parseNumericInput(f.fundedConsistencyRulePercent),
    maxContracts: f.maxContracts ? parseInt(f.maxContracts) : null,
    price: parseNumericInput(f.price),
    commissionPerContract: parseNumericInput(f.commissionPerContract),
    symbolCommissions: symbolRowsToRecord(f.symbolCommissionRows),
  };
}
